/**
 * SIL-1349 - copy for the node Passport drawer (spec section 4.3, handoff
 * section 3). Same discipline as `listingDetail.ts` and `activeVerification.ts`:
 * the component renders layout and imports its sentences from here.
 *
 * What is deliberately NOT in this module, and must not be added to it:
 *
 *  1. **No Active Verification state label or note.** Those are
 *     `AV_STATE_LABELS` / `AV_STATE_NOTES` in `listingDetail.ts`, and the drawer
 *     renders them through `AvStateChip` / `AvStateNote`. A node-scoped
 *     rewording here would be a second string for a user-confirmed one, and the
 *     copy that drifts is the copy that ends up calling an aged-out run a
 *     failure.
 *  2. **No Verified-mark label, and no explainer sentence.** The mark is
 *     `AvStateChip`'s decision via `showsVerifiedMark`; the Tier 3 badge string
 *     is `TIER_3_SELF_REPORTED_LABEL`, owned by
 *     `@siliconx/shared/verification/presentation`; every explainer sentence
 *     lives in `activeVerification.ts` and reaches the drawer through
 *     `ActiveVerificationExplainerTrigger`. `__tests__/activeVerificationCopy.test.ts`
 *     greps the app for those sentences and fails on a component-local copy.
 *  3. **No verdict labels.** `ACTIVE_VERIFICATION_VERDICTS` already carries
 *     label + tone for pass / pass-with-observations / fail.
 */

export const NODE_DRAWER_EYEBROW = 'SILICON PASSPORT';
export const NODE_DRAWER_CLOSE_LABEL = 'Close node Passport';
/** `aria-label` on the sheet itself - it is a dialog and needs a name. */
export const NODE_DRAWER_ARIA_LABEL = 'Node Passport';

export const NODE_DRAWER_LOADING = 'Loading this node’s Passport…';
/** Fallback when the summary fetch fails with no code `errors.ts` translates. */
export const NODE_DRAWER_ERROR_FALLBACK = 'Could not load this node’s Passport.';

// ---------------------------------------------------------------------------
// Sections. A section whose source key is absent from the response is not
// rendered at all, so none of these headings is ever shown over an empty body.
// ---------------------------------------------------------------------------

export const NODE_DRAWER_AV_HEADING = 'Active Verification run';
export const NODE_DRAWER_GPUS_HEADING = 'GPUs in this node';
export const NODE_DRAWER_CUSTODY_HEADING = 'Chain of custody';

export const NODE_DRAWER_GPU_COLUMNS = {
  slot: 'Slot',
  serial: 'Serial',
  attestation: 'Attestation',
} as const;

/**
 * One label per `attestation` value the server derives (SIL-1345).
 *
 * `not_attested` is NOT a failure and must never render in the failure palette:
 * it is what a Tier 2/3 run, or a Tier 1 part that exposes no serial, correctly
 * reports. Only `attested_mismatch` - a BMC-attested serial that did not match -
 * is an adverse finding.
 */
export const NODE_DRAWER_ATTESTATION_LABELS = {
  attested_match: 'Attested · match',
  attested_mismatch: 'Attested · mismatch',
  not_attested: 'Not attested',
} as const;

/** Marks the one slot WebGPU actually measured (SIL-688). */
export const NODE_DRAWER_GPU_MEASURED = 'measured';
/** A slot with no serial exposed - a fail-open probe, never an error. */
export const NODE_DRAWER_GPU_NO_SERIAL = 'No serial exposed';

export const nodeDrawerGpuSlotLabel = (slot: number) => `Slot ${slot}`;

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

/** The outline action. Routes through the EXISTING stamped-artifact route
 * (`usePassportArtifactDownload`), not a new one. */
export const NODE_DRAWER_DOWNLOAD_REPORT = 'Download SX Report';
export const NODE_DRAWER_OPEN_FULL_PASSPORT = 'Open full Passport';
